/**
 * External Dependencies
 */
import React, { Component } from 'react';

/**
 * A single, session-scoped guard so we only ever auto-reload once. This turns a
 * transient render/chunk failure (common on mobile / in-app browsers, where the
 * page would otherwise stay a permanent blank white until a manual refresh)
 * into a silent recovery, without risking a reload loop on a deterministic bug.
 */
const RELOAD_GUARD_KEY = 'app_crash_reloaded';

/**
 * Component ErrorBoundary
 *
 * Top-level safety net. Without one, any uncaught error while rendering the
 * tree unmounts the whole app and leaves an empty #app node (blank white).
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
    this.handleReload = this.handleReload.bind(this);
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // Surface the real trigger so mobile-only crashes stop being invisible.
    // eslint-disable-next-line no-console
    console.error('App crashed:', error, info);

    let alreadyReloaded = null;
    try {
      alreadyReloaded = window.sessionStorage.getItem(RELOAD_GUARD_KEY);
    } catch {
      // Private mode / storage disabled — skip the auto-reload path.
    }

    // First crash of the session (transient render fault or a failed lazy
    // chunk): try one clean reload before falling back to the retry card.
    if (!alreadyReloaded) {
      try {
        window.sessionStorage.setItem(RELOAD_GUARD_KEY, '1');
      } catch {
        // ignore
      }
      window.location.reload();
    }
  }

  handleReload() {
    try {
      window.sessionStorage.removeItem(RELOAD_GUARD_KEY);
    } catch {
      // ignore
    }
    window.location.reload();
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
            textAlign: 'center',
            fontFamily: "'Nunito Sans', -apple-system, sans-serif"
          }}
        >
          <div style={{ maxWidth: '360px' }}>
            <img
              src="/mayarlogo-small.png"
              alt="Mayar"
              style={{ height: '36px', marginBottom: '20px' }}
            />
            <h2 style={{ fontSize: '18px', margin: '0 0 8px', color: '#1a1a1a' }}>
              Gagal memuat halaman
            </h2>
            <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 20px' }}>
              Terjadi gangguan sesaat saat memuat aplikasi. Silakan muat ulang
              halaman.
            </p>
            <button
              type="button"
              onClick={this.handleReload}
              style={{
                background: '#5D5FEF',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                padding: '10px 20px',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Muat ulang halaman
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
